import type { EixoColuna } from '../geometria/anguloExtracao'
import { obstaculosDaRota } from './obstaculosRota'
import type { MapaEstados } from './estadoPecas'
import type { PerfilVeiculo } from './perfilVeiculo'
import type { ProporcoesVitima } from './perfisVitima'
import type { RotaExtracao } from './rotasExtracao'

/** Abaixo disso, a diferença de folga é ruído de ponto flutuante, não efeito real. */
const TOLERANCIA_FOLGA_M = 0.005

export interface EfeitoGeometricoAcao {
  /** folga mínima, em metros; Infinity quando não há obstáculo na rota */
  folgaAntes: number
  folgaDepois: number
  interferenciasAntes: number
  interferenciasDepois: number
  /** a folga ou a contagem de interferências realmente mudou nesta rota */
  mudouGeometricamente: boolean
}

/**
 * Efeito de UMA ação, na MESMA rota/vítima/configuração: compara o estado das
 * peças imediatamente antes e imediatamente depois.
 *
 * Reaproveita `obstaculosDaRota` — o mesmo cálculo que já alimenta "O que está
 * no caminho" — só chamado duas vezes, uma por mapa de estados. Nenhum ângulo
 * ou custo de giro entra aqui: `custoDoGiro` não depende do estado das peças,
 * então inventar uma variação de ângulo a partir de uma ação de banco seria
 * simplesmente falso.
 */
export function efeitoGeometricoDaAcao(
  perfil: PerfilVeiculo,
  eixoColuna: EixoColuna,
  rota: RotaExtracao,
  proporcoes: ProporcoesVitima,
  estadosAntes: MapaEstados,
  estadosDepois: MapaEstados,
): EfeitoGeometricoAcao {
  const antes = obstaculosDaRota(perfil, eixoColuna, rota, proporcoes, estadosAntes)
  const depois = obstaculosDaRota(perfil, eixoColuna, rota, proporcoes, estadosDepois)
  const folgaAntes = antes[0]?.folga ?? Number.POSITIVE_INFINITY
  const folgaDepois = depois[0]?.folga ?? Number.POSITIVE_INFINITY
  const interferenciasAntes = antes.filter((o) => o.interfere).length
  const interferenciasDepois = depois.filter((o) => o.interfere).length

  const folgaMudou =
    Number.isFinite(folgaAntes) !== Number.isFinite(folgaDepois) ||
    Math.abs(folgaDepois - folgaAntes) > TOLERANCIA_FOLGA_M

  return {
    folgaAntes,
    folgaDepois,
    interferenciasAntes,
    interferenciasDepois,
    mudouGeometricamente: folgaMudou || interferenciasAntes !== interferenciasDepois,
  }
}
